/**
 * @fileoverview Registry assembler — single source of truth for the
 * `(appKey, documentKey) → LegalDocument` lookup.
 *
 * Each per-app document file exports a single named constant that this
 * module imports + indexes. Adding a new document = create the data
 * file + add one import + one entry to `LEGAL_DOCUMENTS`. The cross-app
 * contract test (`legalContentRegistry.contract.test.js`) walks the
 * map and validates each document via `validateDocument`.
 *
 * @module @puffin-core/legal-content/registry
 */

import { privacy as parcelPrivacy } from './documents/parcel/privacy.js';
import { terms as parcelTerms } from './documents/parcel/terms.js';
import { privacy as plannerPrivacy } from './documents/planner/privacy.js';
import { terms as plannerTerms } from './documents/planner/terms.js';
import { privacy as palmPrivacy } from './documents/palm/privacy.js';
import { terms as palmTerms } from './documents/palm/terms.js';
import { privacy as paydayPrivacy } from './documents/payday/privacy.js';
import { terms as paydayTerms } from './documents/payday/terms.js';
import { dpa as paydayDpa } from './documents/payday/dpa.js';
import { subprocessors as paydaySubprocessors } from './documents/payday/subprocessors.js';
import { privacy as patrolPrivacy } from './documents/patrol/privacy.js';
import { terms as patrolTerms } from './documents/patrol/terms.js';
import { privacy as portPrivacy } from './documents/port/privacy.js';
import { terms as portTerms } from './documents/port/terms.js';
import { privacy as prepPrivacy } from './documents/prep/privacy.js';
import { terms as prepTerms } from './documents/prep/terms.js';
import { privacy as prospectPrivacy } from './documents/prospect/privacy.js';
import { terms as prospectTerms } from './documents/prospect/terms.js';
import { privacy as purchasePrivacy } from './documents/purchase/privacy.js';
import { terms as purchaseTerms } from './documents/purchase/terms.js';
// No tenant-site tenant is registered here. A white-label tenant's legal pages are TENANT DATA —
// `type: 'legal'` CMS pages named by its config's `settings.legal` (LIFE-SHORTLISTED-001 P3). The
// `protector` and `goldmark` entries moved into their tenant seeds that way once CAP-001 P13 had
// deleted the standalone apps that read them here.

/**
 * The language every document in this registry is written in. There are no locale variants: a
 * marketing site that shows Spanish chrome still renders these documents in English, so the
 * document's own root carries this as its `lang` (WCAG 3.1.2) and a legal page names it as its
 * content language (`<MarketingSeo inLanguage>`) — never a bare literal.
 */
export const LEGAL_DOCUMENT_LANGUAGE = 'en';

/**
 * The frozen, immutable registry. Any consumer that needs the full
 * map (e.g. cross-app contract test) imports this; consumers asking
 * for a specific document use `getLegalDocument` or the
 * `useLegalDocument` hook.
 *
 * Outer keys: appKey (the 9 Colony apps with marketing portals + tenant routes).
 * Inner keys: documentKey (privacy, terms, dpa, subprocessors).
 */
export const LEGAL_DOCUMENTS = Object.freeze({
  parcel: Object.freeze({
    privacy: parcelPrivacy,
    terms: parcelTerms,
  }),
  planner: Object.freeze({
    privacy: plannerPrivacy,
    terms: plannerTerms,
  }),
  palm: Object.freeze({
    privacy: palmPrivacy,
    terms: palmTerms,
  }),
  payday: Object.freeze({
    privacy: paydayPrivacy,
    terms: paydayTerms,
    dpa: paydayDpa,
    subprocessors: paydaySubprocessors,
  }),
  patrol: Object.freeze({
    privacy: patrolPrivacy,
    terms: patrolTerms,
  }),
  port: Object.freeze({
    privacy: portPrivacy,
    terms: portTerms,
  }),
  prep: Object.freeze({
    privacy: prepPrivacy,
    terms: prepTerms,
  }),
  prospect: Object.freeze({
    privacy: prospectPrivacy,
    terms: prospectTerms,
  }),
  purchase: Object.freeze({
    privacy: purchasePrivacy,
    terms: purchaseTerms,
  }),
});

/**
 * Resolve a single document.
 *
 * @param {string} appKey
 * @param {string} documentKey
 * @returns {{ document: import('./types.js').LegalDocument | null, error: Error | null }}
 */
export function getLegalDocument(appKey, documentKey) {
  if (!appKey || typeof appKey !== 'string') {
    return {
      document: null,
      error: new Error('getLegalDocument: appKey must be a non-empty string'),
    };
  }
  if (!documentKey || typeof documentKey !== 'string') {
    return {
      document: null,
      error: new Error('getLegalDocument: documentKey must be a non-empty string'),
    };
  }
  const appDocs = LEGAL_DOCUMENTS[appKey];
  if (!appDocs) {
    return {
      document: null,
      error: new Error(
        `getLegalDocument: unknown appKey "${appKey}" (registered: ${Object.keys(LEGAL_DOCUMENTS).join(', ')})`,
      ),
    };
  }
  const doc = appDocs[documentKey];
  if (!doc) {
    return {
      document: null,
      error: new Error(
        `getLegalDocument: unknown documentKey "${documentKey}" for appKey "${appKey}" (registered: ${Object.keys(appDocs).join(', ')})`,
      ),
    };
  }
  return { document: doc, error: null };
}

/**
 * @returns {string[]} all registered appKeys.
 */
export function listAppKeys() {
  return Object.keys(LEGAL_DOCUMENTS);
}

/**
 * @param {string} appKey
 * @returns {string[]} documentKeys registered for the given app, or empty array if unknown.
 */
export function listDocumentKeys(appKey) {
  const appDocs = LEGAL_DOCUMENTS[appKey];
  return appDocs ? Object.keys(appDocs) : [];
}
